'use client';

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import dynamic from 'next/dynamic';
import { SvgPause } from '@/components/Icons/SvgPause';
import { SvgPlay } from '@/components/Icons/SvgPlay';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

export default function ComfortVideo({ videoUrl }) {
  const [isActive, setIsActive] = useState(false);
  const [playVideo, setPlayVideo] = useState(false);
  const [remountKey, setRemountKey] = useState(0);
  const containerRef = useRef(null);

  const togglePlay = () => {
    setIsActive(true);
    setPlayVideo((p) => !p);
  };

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise. Also autoplay again.
  useLayoutEffect(() => {
    return () => {
      setPlayVideo(true);
      setRemountKey((key) => key + 1);
    };
  }, []);

  useEffect(() => {
    // Skip re-arming the observer once already active in case of history change
    if (isActive) return;

    const pdpTriggerEl = document.getElementById('product-hero');
    if (!pdpTriggerEl) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting === true) {
            setIsActive(true);
            setPlayVideo(true);
            observer.disconnect();
          }
        },
        {
          rootMargin: '0px 0px 300px 0px',
          threshold: 0.1
        }
      );

      observer.observe(containerRef.current);
      return () => observer.disconnect();
    } else {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting === false) {
            setIsActive(true);
            setPlayVideo(true);
            observer.disconnect();
          }
        },
        {
          rootMargin: '200px',
          threshold: 0.1
        }
      );

      observer.observe(pdpTriggerEl);
      return () => observer.disconnect();
    }
  }, [containerRef, isActive]);

  return (
    <div
      ref={containerRef}
      className="relative aspect-[1.5] w-full sm:w-1/2 lg:max-h-[391px]"
    >
      {isActive && (
        <ReactPlayer
          key={remountKey}
          url={videoUrl}
          loop
          playing={playVideo}
          playsinline
          volume={0}
          muted
          width="100%"
          height="100%"
          className="absolute top-0 left-0"
        />
      )}

      {playVideo ? (
        <SvgPause
          handleClick={togglePlay}
          className="absolute right-6 bottom-6 z-10 h-[40px] w-[40px] sm:bottom-6 lg:bottom-6"
        />
      ) : (
        <SvgPlay
          handleClick={togglePlay}
          className="absolute right-6 bottom-6 z-10 h-[40px] w-[40px] sm:bottom-6 lg:bottom-6"
        />
      )}
    </div>
  );
}
